import React, { useState, useEffect, useCallback, useRef } from "react";
import { CircularProgress } from "@mui/material";
import campaignStepOneIcon from "../../assets/images/svg/mab-compgn-7.svg";
import dropOpenIcon from "../../assets/images/svg/mab-drop-open-7.svg";
import downArrow from "../../assets/images/svg/mab-down-arrow.svg";
import ellipseBg from "../../assets/images/svg/mab-ellipse.svg";
import betaBadge from "../../assets/images/svg/mba-beta.svg";
import starIcon from "../../assets/images/svg/mba-star.svg";
import aiInterestIcon from "../../assets/images/svg/ai-interest.svg";
import plusIcon from "../../assets/images/svg/mba-plus.svg";
import cardSelectBg from "../../assets/images/svg/mab-card-select-8.svg";
import cardSelectedBg from "../../assets/images/svg/interest-manual-active.svg";
import interestSelectedBg from "../../assets/images/svg/interest-selected.svg";
import NextButton from "../common/next-button";
import { apiBaseUrl } from "../consts/api-url.const";

type Interest = {
  id: string;
  name: string;
  audience_size?: number;
  path?: string[];
};

type InterestsDropdownProps = {
  id: string;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onNext?: (id: string) => void;
  onInterestsChange?: (values: Interest[]) => void;
  initialInterests?: Array<{ id: string; name: string }>; // For edit mode
  isEditMode?: boolean;
};
const InterestsDropdown: React.FC<InterestsDropdownProps> = ({
  id,
  expandedId,
  onToggle,
  onNext,
  onInterestsChange,
  initialInterests,
  isEditMode = false,
}) => {
  const isOpen = expandedId === id;
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [aiSelectedInterests, setAiSelectedInterests] = useState<Interest[]>(
    [],
  );
  const [manualSelectedInterests, setManualSelectedInterests] = useState<
    Interest[]
  >([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
  const [interests, setInterests] = useState<Interest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize selected interests from props (for edit mode)
  useEffect(() => {
    console.log("🔍 Interests Dropdown useEffect triggered:", {
      initialInterests,
      isEditMode,
      initialInterestsLength: initialInterests?.length || 0,
      willInitialize: !!(initialInterests && initialInterests.length > 0),
    });

    if (initialInterests && initialInterests.length > 0) {
      console.log("✅ Initializing with interests:", initialInterests);
      setManualSelectedInterests(initialInterests as Interest[]);
      // Auto-select the manual card to show the interests
      setSelectedCard("manual");
      // Notify parent component
      if (onInterestsChange) {
        onInterestsChange(initialInterests as Interest[]);
      }
    } else {
      console.log("⚠️ No interests to initialize");
    }
  }, [initialInterests, onInterestsChange]);

  // Keep manual card selected in edit mode when interests exist
  useEffect(() => {
    if (isEditMode && manualSelectedInterests.length > 0) {
      console.log(
        "🔒 Edit mode: Keeping manual card selected with",
        manualSelectedInterests.length,
        "interests",
      );
      setSelectedCard("manual");
    }
  }, [isEditMode, manualSelectedInterests.length]);

  // Fetch interests from Meta API when dropdown opens
  useEffect(() => {
    if (isOpen && interests.length === 0) {
      fetchInterests();
    }
  }, [isOpen]);

  // Fetch all interests from API
  const fetchInterests = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiBaseUrl}/api/meta-builder/interests`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.interests) {
        setInterests(data.interests);
      }
    } catch (error) {
      console.error("Error fetching interests:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Search interests from API with debouncing
  const searchInterests = async (searchQuery: string) => {
    if (!searchQuery || searchQuery.trim().length === 0) {
      fetchInterests(); // Fetch all if search is empty
      return;
    }

    setIsLoading(true);
    try {
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Debounce search
      searchTimeoutRef.current = setTimeout(async () => {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${apiBaseUrl}/api/meta-builder/interests?q=${encodeURIComponent(searchQuery)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = await response.json();

        if (data.interests) {
          setInterests(data.interests);
        }
        setIsLoading(false);
      }, 500);
    } catch (error) {
      console.error("Error searching interests:", error);
      setIsLoading(false);
    }
  };

  const filteredSuggestions = interests.filter((suggestion) =>
    suggestion.name.toLowerCase().includes(inputValue.toLowerCase()),
  );

  const emitInterests = useCallback(() => {
    if (!onInterestsChange) return;
    const combined = [...aiSelectedInterests, ...manualSelectedInterests];
    onInterestsChange(combined);
  }, [aiSelectedInterests, manualSelectedInterests, onInterestsChange]);

  const handleCardClick = (cardType: "ai" | "manual") => {
    if (selectedCard === cardType) {
      // If clicking the same card, unselect it
      setSelectedCard(null);
      setInputValue("");
      setShowDropdown(false);
    } else {
      // Select the clicked card
      setSelectedCard(cardType);
      setInputValue("");
      setShowDropdown(false);
    }
    emitInterests();
  };

  const handleGenerate = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleCardClick("ai");
  };

  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleCardClick("manual");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setShowDropdown(value.length > 0);

    // Search interests from API
    searchInterests(value);
  };

  const handleSelectSuggestion = (suggestion: Interest) => {
    if (!manualSelectedInterests.find((item) => item.id === suggestion.id)) {
      setManualSelectedInterests([...manualSelectedInterests, suggestion]);
    }
    setInputValue("");
    setShowDropdown(false);
    emitInterests();
  };

  const handleRemoveAiInterest = (interest: Interest) => {
    setAiSelectedInterests(
      aiSelectedInterests.filter((item) => item.id !== interest.id),
    );
    emitInterests();
  };

  const handleRemoveManualInterest = (interest: Interest) => {
    setManualSelectedInterests(
      manualSelectedInterests.filter((item) => item.id !== interest.id),
    );
    emitInterests();
  };

  // Emit combined interests to parent
  useEffect(() => {
    emitInterests();
  }, [
    aiSelectedInterests,
    manualSelectedInterests,
    selectedCard,
    emitInterests,
  ]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        inputRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showDropdown]);

  return (
    <div className="w-full">
      <div
        className="relative rounded-[12px] sm:rounded-[16px] md:rounded-[20px] shadow-[0_14px_40px_rgba(0,0,0,0.25)] overflow-hidden w-full"
        style={{
          background: "#878DA11A",
          backgroundImage: `url(${ellipseBg})`,
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-90 transition-opacity min-h-[60px] h-[60px] sm:min-h-[72px] sm:h-[72px] md:min-h-[88px] md:h-[88px] px-4 py-4 sm:px-4 sm:py-4 md:px-6 md:py-6"
          onClick={() => onToggle(id)}
        >
          <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
            <img
              src={isOpen ? dropOpenIcon : campaignStepOneIcon}
              alt="Interests"
              className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0"
            />
            <span
              className="text-[16px] whitespace-nowrap"
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                lineHeight: "130%",
                color: isOpen ? "#58F2A9" : "#FFFFFF",
              }}
            >
              Interests
            </span>
          </div>
          <img
            src={downArrow}
            alt="Toggle"
            className="pointer-events-none transition-transform flex-shrink-0"
            style={{
              height: "9.5px",
              width: "auto",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              filter: isOpen
                ? "brightness(0) saturate(100%) invert(78%) sepia(89%) saturate(1352%) hue-rotate(90deg) brightness(102%) contrast(101%)"
                : "none",
            }}
          />
        </div>

        {isOpen && (
          <div className="flex flex-col transition-all duration-300 px-4 pb-4 sm:px-4 sm:pb-4 md:px-6 md:pb-6">
            <div className="flex flex-col gap-3 md:gap-6 md:ml-[41px]">
              <p
                className="text-white text-[14px] lg:text-[16px] leading-[130%]"
                
              >
                Set up interests by clicking on “+ Add manually”
              </p>
              {/* Cards */}
              <div className="flex flex-col lg:flex-row gap-4 md:gap-6 items-stretch">
                {/* <div
                  className="border rounded-[16px] md:rounded-[20px] shadow-[0_10px_30px_rgba(0,0,0,0.25)] flex flex-col relative overflow-hidden cursor-pointer w-full"
                  onClick={() => !isEditMode && handleCardClick("ai")}
                  style={{
                    width: "100%",
                    backgroundColor:
                      selectedCard === "ai" ? "transparent" : "#202027",
                    backgroundImage:
                      selectedCard === "ai" ? `url(${cardSelectBg})` : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    opacity: isEditMode ? 0.6 : 1,
                    cursor: isEditMode ? "not-allowed" : "pointer",
                  }}
                >
                  <div className="flex flex-col h-full p-4 md:p-6">
                    <div className="flex items-start justify-between gap-2 mb-2 md:mb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <img
                          src={starIcon}
                          alt="AI"
                          className="w-6 h-6 flex-shrink-0"
                        />
                        <span className="text-white font-semibold text-base md:text-[18px] truncate">
                          Generate with AI
                        </span>
                      </div>
                      <img
                        src={betaBadge}
                        alt="Beta"
                        className="h-6 w-auto flex-shrink-0"
                      />
                    </div>

                    <p className="text-white text-sm md:text-[14px] leading-[130%] mb-3 md:mb-4">
                      Automatically find audiences that loved similar music.
                      Outperforms manually selected interests most of the time.
                    </p>
                  </div>
                </div> */}

                <div
                  className="border rounded-[16px] md:rounded-[20px] shadow-[0_10px_30px_rgba(0,0,0,0.25)] flex flex-col relative overflow-hidden cursor-pointer w-full"
                  onClick={() => !isEditMode && handleCardClick("manual")}
                  style={{
                    width: "100%",
                    backgroundColor:
                      selectedCard === "manual" ? "transparent" : "#202027",
                    backgroundImage:
                      selectedCard === "manual"
                        ? `url(${cardSelectedBg})`
                        : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    opacity: isEditMode ? 0.6 : 1,
                    cursor: isEditMode ? "not-allowed" : "pointer",
                  }}
                >
                  <div className="flex flex-col h-full p-4 md:p-6">
                    <div className="flex items-center gap-3 mb-2 md:mb-3">
                      <img
                        src={plusIcon}
                        alt="Add"
                        className="w-6 h-6 flex-shrink-0"
                      />
                      <span className="text-white font-semibold text-base md:text-[18px]">
                        Add manually
                      </span>
                    </div>

                    <p className="text-white text-sm md:text-[14px] leading-[130%] mb-3 md:mb-4">
                      Choose Meta interests according to your selection
                    </p>
                  </div>
                </div>
              </div>

              {selectedCard === "ai" && (
                <div className="flex flex-col relative z-50 mb-4">
                  <div className="mt-3">
                    <button
                      type="button"
                      disabled={isEditMode}
                      className="inline-flex items-center justify-between gap-3 px-6 h-[52px] rounded-full text-white font-semibold text-[20px] leading-none w-full sm:w-auto sm:min-w-[260px]"
                      style={{
                        background: "#63BAAB",
                        opacity: isEditMode ? 0.6 : 1,
                        cursor: isEditMode ? "not-allowed" : "pointer",
                      }}
                    >
                      <span className="text-[16px] leading-none font-semibold">
                        Generate interests
                      </span>
                      <span className="flex items-center justify-center">
                        <img
                          src={aiInterestIcon}
                          alt="Generate"
                          className="w-[17px] h-[20px]"
                        />
                      </span>
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2 max-w-full mt-3 mb-3">
                    {aiSelectedInterests.map((interest) => (
                      <div
                        key={interest.id}
                        onClick={() =>
                          !isEditMode && handleRemoveAiInterest(interest)
                        }
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm transition-all max-w-full"
                        style={{
                          backgroundColor: "rgba(255,255,255,0.12)",
                          opacity: isEditMode ? 0.6 : 1,
                          cursor: isEditMode ? "not-allowed" : "pointer",
                        }}
                      >
                        <span className="truncate max-w-[120px] md:max-w-[160px]">
                          {interest.name}
                        </span>

                        {!isEditMode && (
                          <svg width="12" height="12" viewBox="0 0 12 12">
                            <path
                              d="M9 3L3 9M3 3L9 9"
                              stroke="#878DA1"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedCard === "manual" && (
                <div className="flex flex-col relative z-50 mb-4">
                  {/* Title */}
                  <h3 className="text-white text-sm md:text-[16px] font-medium mb-1">
                    Choose Meta Interests
                  </h3>

                  <div className="w-full transition-all duration-200">
                    {/* ===== INPUT ===== */}
                    <div
                      className="relative transition-all duration-200"
                      style={{
                        marginBottom:
                          !showDropdown && manualSelectedInterests.length > 0
                            ? "6px"
                            : "6px",
                      }}
                    >
                      <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        placeholder="Type to search interests..."
                        disabled={isEditMode}
                        className={`w-full px-3 md:px-4 py-3 md:py-4 pr-11 md:pr-12 text-sm md:text-[16px] outline-none transition-all duration-200 border ${
                          showDropdown
                            ? "rounded-t-lg rounded-b-none border-gray-600"
                            : "rounded-lg border-gray-600"
                        }`}
                        style={{
                          minHeight: "48px",
                          backgroundColor: isEditMode ? "#2A2F3D" : "white",
                          opacity: isEditMode ? 0.6 : 1,
                          cursor: isEditMode ? "not-allowed" : "text",
                        }}
                      />

                      {/* Loader */}
                      {isLoading && (
                        <div className="absolute right-4 top-0 bottom-0 flex items-center justify-center">
                          <CircularProgress
                            size={20}
                            style={{ color: "#888" }}
                          />
                        </div>
                      )}
                    </div>

                    {/* ===== DROPDOWN (NOW IN NORMAL FLOW) ===== */}
                    {showDropdown && (
                      <div className="w-full bg-[#202027] border border-gray-700 border-t-0 rounded-b-lg shadow-2xl z-[100] overflow-hidden transition-all">
                        {filteredSuggestions.length > 0 ? (
                          <div
                            ref={dropdownRef}
                            className="max-h-[180px] md:max-h-[192px] overflow-y-auto"
                          >
                            {filteredSuggestions.map((suggestion) => (
                              <div
                                key={suggestion.id}
                                onClick={() =>
                                  handleSelectSuggestion(suggestion)
                                }
                                className="px-3 md:px-4 h-[44px] md:h-[48px] flex items-center cursor-pointer hover:bg-[#58F2A9]/20 text-white text-sm md:text-[14px] transition-colors border-b border-gray-700/50 last:border-b-0"
                              >
                                {suggestion.name}
                              </div>
                            ))}
                          </div>
                        ) : (
                          inputValue.length > 0 && (
                            <div className="px-4 py-6 text-center">
                              <p className="text-white text-[14px] font-medium">
                                No interests found
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    )}

                    {/* ===== SELECTED INTERESTS ===== */}
                    <div
                      className={`flex flex-wrap gap-2 max-w-full transition-all duration-200 ${
                        showDropdown ? "mt-5 mb-3" : "mt-3 mb-3"
                      }`}
                    >
                      {manualSelectedInterests.map((interest) => (
                        <div
                          key={interest.id}
                          onClick={() =>
                            !isEditMode && handleRemoveManualInterest(interest)
                          }
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm transition-all max-w-full"
                          style={{
                            backgroundColor: "rgba(255,255,255,0.12)",
                            opacity: isEditMode ? 0.6 : 1,
                            cursor: isEditMode ? "not-allowed" : "pointer",
                          }}
                        >
                          <span className="truncate max-w-[120px] md:max-w-[160px]">
                            {interest.name}
                          </span>

                          {!isEditMode && (
                            <svg width="12" height="12" viewBox="0 0 12 12">
                              <path
                                d="M9 3L3 9M3 3L9 9"
                                stroke="#878DA1"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {/* Helper text below input - only when a card is selected */}
              {selectedCard && (
                <p className="text-white lg:text-[14px] text-[12px] mt-[-48px] mb-1">
                  {selectedCard === "ai"
                    ? "Interests are taken from Meta interest database with help of AI"
                    : "Interests are taken from Meta database"}
                </p>
              )}

              {/* ===== FOOTER WITH NEXT BUTTON (Always visible when dropdown is open) ===== */}
              <div className="flex flex-1 gap-3 mt-2 transition-all justify-end">
                <div className="order-1 sm:order-2 w-full sm:w-auto">
                  <NextButton
                    size="sm"
                    className="w-full md:w-auto"
                    onClick={() => onNext?.(id)}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterestsDropdown;
